import {
    AfterViewChecked,
    AfterViewInit,
    Directive,
    ElementRef,
    OnDestroy,
    OnInit,
    Renderer2
} from "@angular/core";
import { Subject, distinctUntilChanged, fromEvent, takeUntil } from 'rxjs';

@Directive({
    selector: '[smexColumnResize]'
})
export class ColumnResizeDirective implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

    private startX!: number;
    private isResizing = false;
    private initialWidth!: number;
    private table: HTMLElement | null = null;
    private tableWidth: number | null = null;
    private column: HTMLElement;
    private resizer!: HTMLElement;
    private resizerWidth: number | null = null;

    private onDestroy$ = new Subject<void>();

    constructor(private elementRef: ElementRef, private renderer: Renderer2) {
        this.column = this.elementRef?.nativeElement as HTMLElement;
    }

    ngOnInit(): void {
        this.createResizer();
        this.renderer.setStyle(this.column, 'position', `relative`);
        const mousedown = fromEvent<MouseEvent>(this.resizer, 'mousedown');
        mousedown.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
                 .subscribe(event => this.onMouseDown(event));
        // prevent click event
        fromEvent<MouseEvent>(this.resizer, "click")
            .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
            .subscribe(event => event.stopPropagation());
    }

    ngAfterViewInit() {
        this.table = this.findParentTable(this.elementRef.nativeElement);
    }

    ngAfterViewChecked() {
        if (this.resizerWidth === null) {
            if (this.column.parentElement?.lastElementChild == this.column) {
                this.resizerWidth = 15;
            } else {
                this.resizerWidth = 5;
            }
            this.renderer.setStyle(this.resizer, 'width', `${this.resizerWidth}px`);
        }
        if (this.table && this.tableWidth === null) {
            this.tableWidth = this.table!.offsetWidth;
            const headerCells = this.column.parentElement?.querySelectorAll(`th:has(div.column-resizer)`);
            if (headerCells?.item(0) == this.column) {
                setTimeout(() => {
                    this.tableWidth = this.table!.offsetWidth;
                    const row = this.column?.parentElement;
                    const cells = Array.from(row?.children as any);
                    cells.forEach(cell => {
                        if (!(cell as HTMLElement).style.width) {
                            this.renderer.setStyle(cell, 'width', (cell as HTMLElement).offsetWidth + 'px');
                        }
                    });
                    this.renderer.setStyle(this.table, 'table-layout', `fixed`);
                    this.renderer.setStyle(this.table, 'width', this.tableWidth + 'px');
                }, 200);
            }
        }
    }

    private createResizer() {
        this.resizer = this.renderer.createElement('div');
        this.renderer.addClass(this.resizer, 'column-resizer');
        this.renderer.appendChild(this.column, this.resizer);
        this.renderer.setStyle(this.resizer, 'position', `absolute`);
        this.renderer.setStyle(this.resizer, 'top', `0`);
        this.renderer.setStyle(this.resizer, 'right', `0`);
        this.renderer.setStyle(this.resizer, 'bottom', `0`);
        this.renderer.setStyle(this.resizer, 'cursor', `col-resize`);
        this.renderer.setStyle(this.resizer, 'z-index', `1`);
    }

    private onMouseDown(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.startX = event?.pageX;
        this.isResizing = true;
        this.initialWidth = this.column?.offsetWidth;
        this.renderer.setStyle(this.resizer, 'width', `100%`);

        if (this.table) {
            // this.renderer.addClass(this.table, 'table-resizing');
            this.renderer.setStyle(this.table, 'table-layout', `fixed`);
            this.renderer.setStyle(this.table, 'cursor', `col-resize`);
            this.renderer.setStyle(this.table, 'user-select', `none`);
            const tableWidth = this.table?.offsetWidth;

            const onMouseMove = (moveEvent: MouseEvent) => {
                moveEvent.preventDefault();
                moveEvent.stopPropagation();
                if (this.isResizing) {
                    const deltaX = moveEvent?.pageX - this.startX;
                    const newWidth = this.initialWidth + deltaX;
                    // Restrict the column width to a minimum of 40
                    if (newWidth >= 40) {
                        const widthStr = `${newWidth}px`;

                        // Update the width of the current column
                        this.renderer.setStyle(this.column, 'width', widthStr);

                        // Adjust the width of the table if it has a fixed width
                        if (tableWidth > 0) {
                            this.renderer.setStyle(this.table, 'width', tableWidth + deltaX + 'px');
                        }
                    }
                }
            };

            const onMouseUp = (moveEvent: MouseEvent) => {
                moveEvent.preventDefault();
                moveEvent.stopPropagation();
                this.isResizing = false;
                this.renderer.setStyle(this.resizer, 'width', `${this.resizerWidth}px`);
                this.renderer.removeStyle(this.resizer, 'border-right');
                if (this.table) {
                    this.renderer.removeStyle(this.table, 'cursor');
                    this.renderer.removeStyle(this.table, 'user-select');
                }
                document.removeEventListener('mousemove', onMouseMove, true);
                document.removeEventListener('mouseup', onMouseUp, true);
            };

            document.addEventListener('mousemove', onMouseMove, true);
            document.addEventListener('mouseup', onMouseUp, true);
        }
    }

    private findParentTable(element: HTMLElement): HTMLElement | null {
        while (element) {
            if (element.tagName === 'TABLE') {
                return element;
            }
            if (element?.parentElement) {
                element = element?.parentElement;
            } else {
                break;
            }
        }
        return null;
    }

    ngOnDestroy(): void {
        // we've destroyed the component, so update the subject
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
}
